import { ensureRouteData } from '@/app/query-client'
import { stockPricesQueryOptions } from '@/features/prices/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { stockPricesQuerySchema } from '@/schemas/prices'

/**
 * The one parse both the loader and `StockPricesScreen` read `search`
 * through.
 *
 * `Route.useSearch()` merges the URL's raw query string into the validated
 * object — an unrecognised param (`?utm_source=...`) rides along even though
 * `stockPricesQuerySchema` never named it. `stockPricesQueryOptions` keys on
 * the WHOLE query object, so that extra field would otherwise make the
 * screen's key differ, structurally, from the one this loader warmed for the
 * entry URL: a second request, and a visible pending-then-loaded flash on a
 * link nobody meant to change anything.
 */
export const toStockPricesQuery = (search: unknown) => stockPricesQuerySchema.parse(search)

/**
 * The `/market/stock-prices` loader.
 *
 * MAIN, keyed on the URL's own `query`/`sortBy`/`sortDesc`/`page`/`pageSize` —
 * awaited for the ENTRY search only, per the "Search params" rule: there is
 * no `loaderDeps`, so a keystroke in the search box or a pager click re-runs
 * no loader at all. `StockPricesScreen` keeps reading through the plain
 * `useStockPrices` (`placeholderData: keepPreviousData`, unchanged by this
 * conversion) rather than a suspense query — a search-only change must dim
 * the table in place, not suspend the whole route to its pending skeleton.
 *
 * `stockPricesQuerySchema` is imported straight from `@/schemas/prices`
 * rather than re-exported from here: unlike Stock's `?tab=`, it already lived
 * in a shared schema module before this conversion, so the route's
 * `validateSearch` and this parse already read the one canonical schema and
 * cannot disagree.
 *
 * No secondary reads: the page's only other slots (the search box, the day
 * range legend) carry no data of their own.
 */
export async function loadStockPrices({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const search = toStockPricesQuery(location.search)
  await ensureRouteData(queryClient, stockPricesQueryOptions(search))
}
